import { DestroyRef, effect, inject, Injectable, untracked } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { EventAlarmsResponse, EventDetail } from '../models/entities/event-detail.model';
import { AgentDataService } from './agent-data.service';
import { BrowserTabService } from './browser-tab.service';
import { EVENT_CACHE_CATALOG_ID, EventDataCacheService } from './event-data-cache.service';
import { ProcessingTimerService } from './processing-timer.service';

/**
 * Background-loads inactive workspace tab payloads so a tab switch is a cache hit.
 * After that initial load, alarms (and flagged state) are polled on the app refresh
 * interval so background-tab claims stay alive. Those polls request `alarmStatus=New`
 * and append onto the cached list.
 */
@Injectable({ providedIn: 'root' })
export class EventWorkspacePrefetchService {
  private readonly browserTabs = inject(BrowserTabService);
  private readonly agentData = inject(AgentDataService);
  private readonly cache = inject(EventDataCacheService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly processingTimers = inject(ProcessingTimerService);
  private prefetchQueued = false;
  private alarmPollTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    effect(() => {
      this.browserTabs.tabDetails();
      this.browserTabs.activeTabId();
      if (this.prefetchQueued) {
        return;
      }
      this.prefetchQueued = true;
      untracked(() => {
        queueMicrotask(() => {
          this.prefetchQueued = false;
          void this.prefetchInactiveTabs();
        });
      });
    });

    this.destroyRef.onDestroy(() => this.stopAlarmPolling());
  }

  async prefetchInactiveTabs(): Promise<void> {
    await this.prefetchCatalogs();

    const activeTabId = this.browserTabs.activeTabId();
    const jobs = this.browserTabs
      .tabDetails()
      .filter((entry) => entry.tab.id !== activeTabId && !!entry.pageState.activeEventId)
      .map((entry) =>
        this.prefetchEvent(entry.pageState.activeEventId!, entry.pageState.activeEventSiteId),
      );

    await Promise.all(jobs);
    this.startAlarmPolling();
  }

  private async prefetchCatalogs(): Promise<void> {
    await Promise.allSettled([
      this.cache.loadCached('raiseOutcomes', EVENT_CACHE_CATALOG_ID, null, () =>
        firstValueFrom(this.agentData.getRaiseEventOutcomes()),
      ),
      this.cache.loadCached('closeOutcomes', EVENT_CACHE_CATALOG_ID, null, () =>
        firstValueFrom(this.agentData.getCloseEventOutcomes()),
      ),
      this.cache.loadCached('disarmReasons', EVENT_CACHE_CATALOG_ID, null, () =>
        firstValueFrom(this.agentData.getSiteDisarmReasons()),
      ),
      this.cache.loadCached('cameraDisarmReasons', EVENT_CACHE_CATALOG_ID, null, () =>
        firstValueFrom(this.agentData.getCameraDisarmReasons()),
      ),
    ]);
  }

  private async prefetchEvent(
    eventId: string,
    siteId: number | null,
  ): Promise<void> {
    const jobs: Promise<unknown>[] = [
      this.cache.loadCached('detail', eventId, null, () =>
        firstValueFrom(this.agentData.getEventDetail(eventId)),
      ),
      this.cache.loadCached('alarms', eventId, null, () =>
        firstValueFrom(this.agentData.getEventAlarms(eventId)),
      ),
    ];

    if (siteId != null && siteId > 0) {
      jobs.push(
        this.cache.loadCached('cameras', eventId, siteId, () =>
          firstValueFrom(this.agentData.getEventCameras(siteId)),
        ),
        this.cache.loadCached('talkdowns', eventId, siteId, () =>
          firstValueFrom(this.agentData.getEventTalkdowns(siteId)),
        ),
        this.cache.loadCached('site', String(siteId), null, () =>
          firstValueFrom(this.agentData.getDealerSite(siteId)),
        ),
      );
    }

    await Promise.allSettled(jobs);
    this.applyTabChrome(eventId);
    this.applyClaimedAtFromDetail(eventId);
  }

  /** Writes location/tags onto the tab that owns the event — not the active tab. */
  private applyTabChrome(eventId: string): void {
    const tab = this.browserTabs.findTab(eventId);
    if (!tab) {
      return;
    }

    const location = this.cache.read<EventDetail>('detail', eventId)?.location?.trim();
    const tagLabels = [
      ...new Set(
        (this.cache.read<EventAlarmsResponse>('alarms', eventId)?.items ?? []).flatMap((alarm) =>
          (alarm.aiTags ?? []).map((tag) => tag.label),
        ),
      ),
    ].filter((label) => label.length > 0);

    if (!location && tagLabels.length === 0) {
      return;
    }

    this.browserTabs.updateTabDisplay(tab.id, {
      ...(location ? { label: location } : {}),
      ...(tagLabels.length ? { tags: tagLabels } : {}),
    });
  }

  /** Starts after the first prefetch so the first tick does not double-fetch that load. */
  private startAlarmPolling(): void {
    if (this.alarmPollTimer != null || environment.refreshIntervalMs <= 0) {
      return;
    }

    this.alarmPollTimer = setInterval(
      () => void this.pollInactiveTabAlarms(),
      environment.refreshIntervalMs,
    );
  }

  private stopAlarmPolling(): void {
    if (this.alarmPollTimer == null) {
      return;
    }
    clearInterval(this.alarmPollTimer);
    this.alarmPollTimer = null;
  }

  private async pollInactiveTabAlarms(): Promise<void> {
    const activeTabId = this.browserTabs.activeTabId();
    const eventIds = this.browserTabs
      .tabDetails()
      .filter((entry) => entry.tab.id !== activeTabId && !!entry.pageState.activeEventId)
      .map((entry) => entry.pageState.activeEventId!);

    await Promise.all(eventIds.map((eventId) => this.pollEventAlarms(eventId)));
  }

  private async pollEventAlarms(eventId: string): Promise<void> {
    const existing = this.cache.read<EventAlarmsResponse>('alarms', eventId);
    try {
      const result = await firstValueFrom(
        this.agentData.getEventAlarms(
          eventId,
          existing ? { alarmStatus: 'New', mergeInto: existing } : undefined,
        ),
      );
      this.cache.write('alarms', eventId, result);
      this.applyTabChrome(eventId);
    } catch {
      // Leave the last successful payload; the next interval retries this event.
    }
  }

  /** Corrects a now-fallback timer on the tab that owns this event. */
  private applyClaimedAtFromDetail(eventId: string): void {
    const tab = this.browserTabs.findTab(eventId);
    const claimedAt = this.cache.read<EventDetail>('detail', eventId)?.claimedAt?.trim();
    if (!tab || !claimedAt) {
      return;
    }

    this.processingTimers.startForTab(tab.id, {
      entityId: eventId,
      entityType: this.browserTabs.getPageState(tab.id).eventProcessingMode,
      claimedAt,
    });
  }
}
